import { useCurrentWorkspace } from '@/providers/WorkspaceProvider';
import { useLoading } from '@/providers/LoadingProvider';
import { useEffect, useState } from 'react';
import { TeamOutline, UserInvitation } from '@/hooks/api/models';
import { useUserInvitations, useUserInvitationsInvalidate } from '@/hooks/api/0470_user_invitations';
import { useUpdateUserInvitation } from '@/hooks/api/0480_update_user_invitation';
import { useDestroyUserInvitation } from '@/hooks/api/0481_destroy_user_invitation';
import { toastSuccess } from '@/lib/toast';
import { ModalConfirm } from '@/components/element/modal/ModalConfirm';
import { dateTimeFormat } from '@/utils';
import { Link } from 'react-router-dom';
import { AdminBadge } from '@/features/w/[identifier]/setting/members/{components}/AdminBadge';

export const UserInvitationsElement = () => {
  const { _w_, me } = useCurrentWorkspace();
  const { setShowLoading } = useLoading();
  const [selectedInvitation, setSelectedInvitation] = useState<UserInvitation>();
  const { data } = useUserInvitations(_w_!.identifier);
  const { invalidate } = useUserInvitationsInvalidate(_w_!.identifier);
  const { mutate: updateMutate, isSuccess: updateIsSuccess, isPending: updateIsPending } = useUpdateUserInvitation();
  const {
    mutate: destroyMutate,
    isSuccess: destroyIsSuccess,
    isPending: destroyIsPending,
  } = useDestroyUserInvitation();

  const resendConfirmOk = () => {
    updateMutate({ identifier: _w_!.identifier, id: selectedInvitation!.id });
  };

  const deleteConfirmOk = () => {
    destroyMutate({ identifier: _w_!.identifier, id: selectedInvitation!.id });
  };

  useEffect(() => {
    if (updateIsSuccess) {
      toastSuccess('招待を再送しました');
      invalidate(); // UserInvitationsのキャッシュクリア
    }
  }, [updateIsSuccess]);

  useEffect(() => {
    if (destroyIsSuccess) {
      toastSuccess('招待を取り消しました');
      invalidate(); // UserInvitationsのキャッシュクリア
    }
  }, [destroyIsSuccess]);

  useEffect(() => setShowLoading(updateIsPending || destroyIsPending), [updateIsPending, destroyIsPending]);

  if (data == null || data.length == 0) return <></>;

  return (
    <div className="card mb-4">
      <div className="card-header d-flex align-items-center">
        <div className="col-md-8">
          <h5 className="mb-0">招待中のユーザー一覧</h5>
        </div>
      </div>

      <ModalConfirm
        modalId={'resendConfirm'}
        okClicked={resendConfirmOk}
        message={'招待を再送します。よろしいですか？'}
      />

      <ModalConfirm
        modalId={'deleteConfirm'}
        okClicked={deleteConfirmOk}
        message={'招待を削除します。よろしいですか？'}
      />

      <div className="card-body pt-0">
        <div className="table-responsive">
          <table className="table align-items-center">
            <thead>
              <tr>
                <th className="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7 px-2">
                  メールアドレス
                </th>
                <th className="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7 px-2">権限</th>
                <th className="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7 px-2">チーム</th>
                <th
                  className="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7 px-2"
                  style={{ width: 120 }}
                >
                  招待日時
                </th>
                <th className="text-secondary opacity-7" style={{ width: 100 }}></th>
              </tr>
            </thead>
            <tbody>
              {data.map((invitation) => (
                <tr key={`invitation-${invitation.id}`}>
                  <td className="text-xs">{invitation.email}</td>
                  <td className="text-sm">
                    <AdminBadge admin={invitation.admin} />
                  </td>
                  <td>
                    {invitation.team_outlines
                      .filter((d, i, outlines) => outlines.findIndex((_d) => _d.id == d.id) == i)
                      .map((t) => (
                        <span key={`i-${invitation.id}-t-${t.id}`} className="badge bg-gradient-dark me-1">
                          {t.name}
                        </span>
                      ))}
                  </td>
                  <td className="text-xs">{dateTimeFormat({ date: invitation.expired_at })}</td>
                  <td>
                    {me!.admin && (
                      <>
                        <Link
                          to=""
                          className="btn btn-icon-only btn-outline-info"
                          data-bs-toggle="modal"
                          data-bs-target={`#resendConfirm`}
                          onClick={() => setSelectedInvitation(invitation)}
                        >
                          <i className="fas fa-paper-plane text-info text-sm"></i>
                        </Link>
                        <Link
                          to=""
                          className="btn btn-icon-only btn-outline-danger ms-1"
                          data-bs-toggle="modal"
                          data-bs-target={`#deleteConfirm`}
                          onClick={() => setSelectedInvitation(invitation)}
                        >
                          <i className="fas fa-trash-alt text-danger text-sm"></i>
                        </Link>
                      </>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};
